import React, { useEffect } from 'react';
import './XPInfoModal.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * XP explainer — opens from the Victory Moment "ⓘ" icon and from the
 * /leaderboard footer. Visual cards over paragraph text so students can
 * scan the rules in 3 seconds (per the "no typing, lean visual" design
 * memory). No interactive content — close-on-tap, close-on-Esc.
 */
const XPInfoModal: React.FC<Props> = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="xpinfo-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="xpinfo-card" onClick={(e) => e.stopPropagation()}>
        <button className="xpinfo-close" onClick={onClose} aria-label="Close">×</button>
        <h3 className="xpinfo-title">How points work</h3>

        <div className="xpinfo-rule">
          <div className="xpinfo-icon">✅</div>
          <div className="xpinfo-text">
            <div className="xpinfo-headline">100 points per correct answer</div>
            <div className="xpinfo-sub">Every right answer counts — practice quizzes, modules, and diagnostics.</div>
          </div>
        </div>

        <div className="xpinfo-rule">
          <div className="xpinfo-icon">📈</div>
          <div className="xpinfo-text">
            <div className="xpinfo-headline">Bonus points when mastery climbs</div>
            <div className="xpinfo-sub">Improving on a topic earns extra points — reward for getting better, not just busy.</div>
          </div>
        </div>

        <div className="xpinfo-rule">
          <div className="xpinfo-icon">📜</div>
          <div className="xpinfo-text">
            <div className="xpinfo-headline">
              Real exam questions count <span className="xpinfo-mult">1.2×</span>
            </div>
            <div className="xpinfo-sub">Past-ESLCE practice rewards the most — that's the real game.</div>
          </div>
        </div>

        <div className="xpinfo-rule">
          <div className="xpinfo-icon">🗓️</div>
          <div className="xpinfo-text">
            <div className="xpinfo-headline">Two boards: All-time &amp; Last 7 days</div>
            <div className="xpinfo-sub"><strong>All-time</strong> counts every point since you joined — nothing is ever lost. <strong>Last 7 days</strong> is a fresh weekly race that resets each day.</div>
          </div>
        </div>

        <div className="xpinfo-example">
          <div className="xpinfo-example-label">Example</div>
          <div className="xpinfo-example-body">
            10 past-exam answers · 8 correct
            <br />
            = 8 × 100 × 1.2 = <strong>960 points</strong> + any mastery bonus
          </div>
        </div>

        <button className="xpinfo-got-it" onClick={onClose}>Got it</button>
      </div>
    </div>
  );
};

export default XPInfoModal;
